'use client';
import React, { useEffect, useState, useRef } from 'react';
import Styles from './payment-options.module.scss';
import { formatCurrency } from '~/utils/utils';
import {
  useClearErrorMessage,
  useErrorMessage,
  useSetErrorMessage,
  useSetUserSelectedAmount,
  useSelectedAmountOption,
  useSetSelectedAmountOption,
  usePaymentReview,
} from '~/store/wmap-store';
import {
  INPUT_ID_OTHER_AMOUNT,
  INPUT_ID_OUTSTANDING_AMOUNT,
  LABEL_ID_OTHER_AMOUNT,
  LABEL_ID_OUTSTANDING_AMOUNT,
  paymentNote,
} from '~/constant/constants';

interface PaymentOptionsProps {
  outStandingAmount: number;
}

const PaymentOptions: React.FC<PaymentOptionsProps> = ({
  outStandingAmount,
}) => {
  const activeOption = useSelectedAmountOption();
  const setActiveOption = useSetSelectedAmountOption();
  const [otherAmount, setOtherAmount] = useState('');
  const [
    errorMessage,
    setErrorMessage,
    clearErrorMessage,
    setUserSelectedAmount,
  ] = [
    useErrorMessage(),
    useSetErrorMessage(),
    useClearErrorMessage(),
    useSetUserSelectedAmount(),
  ];

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setUserSelectedAmount(outStandingAmount);
  }, [outStandingAmount, setUserSelectedAmount]);

  const divClass = errorMessage
    ? 'mfrmFormGroup w-100 formError mb-0'
    : 'mfrmFormGroup w-100 mb-0';

  const handleLabelClick = (option: string) => {
    setActiveOption(option);

    if (option === INPUT_ID_OTHER_AMOUNT) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      setUserSelectedAmount(outStandingAmount);
      clearErrorMessage();
    }
  };
  useEffect(() => {
    if (activeOption === INPUT_ID_OTHER_AMOUNT) {
      const inputValue = inputRef.current?.value;
      if (inputValue && inputValue.trim() !== '') {
        validateAmount(otherAmount);
        setUserSelectedAmount(
          Math.min(parseFloat(inputValue), outStandingAmount),
        );
      }
    } else {
      setUserSelectedAmount(outStandingAmount);
      clearErrorMessage();
    }
  }, [activeOption, otherAmount, outStandingAmount, setUserSelectedAmount]);
  //do not render this component for payment review page
  const paymentReview = usePaymentReview();
  if (paymentReview.review) {
    return <></>;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    value.toString();
    if (value === '') {
      setOtherAmount('');
      return;
    }
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < 0) return;
    const decimalIndex = value.indexOf('.');
    if (decimalIndex !== -1) {
      const decimals = value.slice(decimalIndex + 1);
      if (decimals.length > 2) {
        return;
      }
    }

    setOtherAmount(value);
    validateAmount(value);
  };

  const validateAmount = (value: string) => {
    const numericValue = Number(value);
    if (numericValue > outStandingAmount) {
      setErrorMessage(
        `Enter an amount less than ${formatCurrency(outStandingAmount)}`,
      );
    } else if (numericValue <= 0) {
      setErrorMessage(`Enter an amount greater than $0`);
    } else {
      setUserSelectedAmount(numericValue);
      clearErrorMessage();
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    if (activeOption === INPUT_ID_OTHER_AMOUNT && value === 0) {
      setErrorMessage('Enter an amount greater than $0');
    }
  };
  return (
    <section>
      <h4 tabIndex={0}>Amount To Pay</h4>
      <div className={Styles.amountToPayWrapper}>
        {/* Label for Balance */}
        <label
          className={`${Styles.radioLabel} ${
            activeOption === INPUT_ID_OUTSTANDING_AMOUNT ? Styles.checked : ''
          }`}
          id={LABEL_ID_OUTSTANDING_AMOUNT}
          htmlFor={INPUT_ID_OUTSTANDING_AMOUNT}
          onMouseDown={() => handleLabelClick(INPUT_ID_OUTSTANDING_AMOUNT)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleLabelClick(INPUT_ID_OUTSTANDING_AMOUNT);
            }
          }}
        >
          <input
            type="radio"
            id={INPUT_ID_OUTSTANDING_AMOUNT}
            name="outStandingBalance"
            checked={activeOption === INPUT_ID_OUTSTANDING_AMOUNT}
            onChange={() => handleLabelClick(INPUT_ID_OUTSTANDING_AMOUNT)}
          />
          <div className={Styles.radioIcon}>
            <span className={Styles.label}>Balance</span>
          </div>
          <span className={Styles.amount}>
            {formatCurrency(outStandingAmount)}
          </span>
        </label>

        {/* Label for Other Amount */}
        <label
          className={`${Styles.radioLabel} ${
            activeOption === INPUT_ID_OTHER_AMOUNT ? Styles.checked : ''
          }`}
          id={LABEL_ID_OTHER_AMOUNT}
          tabIndex={0}
          htmlFor={INPUT_ID_OTHER_AMOUNT}
          onClick={() => handleLabelClick(INPUT_ID_OTHER_AMOUNT)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleLabelClick(INPUT_ID_OTHER_AMOUNT);
            }
            if (e.key === 'Tab') {
              return;
            }
          }}
        >
          <input
            type="radio"
            id={INPUT_ID_OTHER_AMOUNT}
            name="otherAmount"
            checked={activeOption === INPUT_ID_OTHER_AMOUNT}
            onChange={() => handleLabelClick(INPUT_ID_OTHER_AMOUNT)}
            tabIndex={-1} // Prevent input from being focused directly
          />
          <div className={Styles.radioIcon}>
            <span className={Styles.label}>Other Amount</span>
          </div>
        </label>
        <div
          className={`${Styles.textInputWrapper} ${
            activeOption === INPUT_ID_OTHER_AMOUNT ? Styles.open : Styles.close
          }`}
        >
          <div className={Styles.textInput}>
            <div className={divClass}>
              <input
                type="number"
                id="radioTextInput"
                inputMode="decimal"
                placeholder="0.00"
                aria-invalid={!!errorMessage}
                // REMOVE onFocus here!
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                ref={inputRef}
                value={otherAmount}
                onWheel={(e) => e.currentTarget.blur()}
                tabIndex={activeOption === INPUT_ID_OTHER_AMOUNT ? 0 : -1}
              />
              <label className="focused" htmlFor="radioTextInput">
                Amount (USD) *
              </label>
              <p className={Styles.error} role="alert" aria-live="assertive">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>
        <span
          className={Styles.note}
          role="note"
          tabIndex={0}
          aria-live="assertive"
        >
          {paymentNote}
        </span>
      </div>
    </section>
  );
};

export default PaymentOptions;
